import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { Input, InfoTooltip } from 'ui';
import { Button, Select, Tag } from 'antd';
import {
  determineOperatorAndValue,
  encodeContainsOperator,
  decodeContainsOperator,
} from 'utils/helpers';
import {
  leftParenthesisAlternative,
  rightParenthesisAlternative,
  searchOperators,
} from 'utils/app-configs';
import { injectIntl } from 'react-intl';

const Title = styled.div`
  font-weight: 400;
  font-size: 16px;
`;

const StyledInput = styled(Input)`
  width: 50%;
`;

const StyledSelect = styled(Select)`
  vertical-align: top;
  width: 20%;
  font-size: 14px !important;
  .ant-select-selector {
    height: 34px !important;
    font-size: 14px !important;
    border-radius: 2px !important;
  }
`;

const ApplyButton = styled(Button)`
  vertical-align: top;
  width: ${p => p.isColumnFilter ? '28%' : '18%'} !important;
  text-transform: uppercase;
  margin-left: 2%;
  color: white;
  background-color: #6f7e95;
  &:hover {
    opacity: 0.7;
    color: white;
    background-color: #6f7e95;
  }
  &:active {
    color: white;
    background-color: #6f7e95;
  }
  &:focus {
    color: white;
    background-color: #6f7e95;
  }
`;

const Container = styled.div`
  position: relative;
`;

const FilterTag = styled(Tag)`
  position: absolute;
  left: 21%;
  top: ${p => p.isColumnFilter ? '5' : '24'}px !important;
`;

const OperatorFilter = ({
  description,
  operators,
  onChange,
  activeFilters,
  prefix,
  defaultOperator = searchOperators.contains.id,
  intl,
  isColumnFilter,
  info = '',
}) => {
  const [operator, setOperator] = useState(defaultOperator);
  const [input, setInput] = useState('');
  const [tag, setTag] = useState('');
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    if (activeFilters?.length && activeFilters[0] !== input) {
      if (!activeFilters[0] || !input) {
        let [filter] = activeFilters;
        const regexP = `^${leftParenthesisAlternative}.*${rightParenthesisAlternative}$`;
        const match = new RegExp(regexP);
        if (activeFilters[0].match(match)) {
          filter = decodeContainsOperator(filter);
        }
        const result = determineOperatorAndValue(filter);
        setOperator(result.operator);
        setTag(`${prefix}:${filter}`);
        setInput('');
      }
    } else {
      setTag('');
      setOperator(defaultOperator);
    }
  }, [activeFilters]);

  const handleOperatorChange = (value) => {
    if (tag) {
      const result = determineOperatorAndValue(tag.replace(`${prefix}:`, ''));
      setInput(result.value);
      setTag('');
    }
    setDirty(true);
    setOperator(value);
  };

  const handleInputChange = (e) => {
    setDirty(true);
    setInput(e.target.value);
  };

  const handleTagClear = () => {
    setTag('');
    setInput('');
    setOperator(defaultOperator);
    onChange([]);
  };

  const handleApply = () => {
    if (input) {
      const currentOperator = operators?.find(o => o.id === operator);
      let value = currentOperator.shortHand.replace(/{value}/, input);
      setInput('');
      setDirty(false);
      setTag(`${prefix}:${value}`);
      if (operator === searchOperators.contains.id) {
        // since () have a special purpose in react router and encoding the url does not encode the
        // () we just replace the () with an alternative
        value = encodeContainsOperator(value);
      }
      onChange([value]);
    } else {
      handleTagClear();
    }
  };

  const handleFocus = () => {
    if (tag) {
      const result = determineOperatorAndValue(tag.replace(`${prefix}:`, ''));
      setInput(result.value);
      setTag('');
    }
  };

  const handleBlur = () => {
    if (!dirty) {
      if (input) {
        const currentOperator = operators?.find(o => o.id === operator);
        const value = currentOperator.shortHand.replace(/{value}/, input);
        setInput('');
        setDirty(false);
        setTag(`${prefix}:${value}`);
      }
    }
  };

  return <Container>
    {!isColumnFilter && <Title>
      {description.toUpperCase()}
      <InfoTooltip content={info} />
    </Title>}
    <StyledSelect
      options={operators?.map(o => ({
        value: o.id,
        label: o.description,
        shortHand: o.shortHand,
      }))}
      value={operator}
      onSelect={(e, o) => handleOperatorChange(o.value)}
      dropdownMatchSelectWidth={false}
      // Neede to keep filter open, more info:
      // eslint-disable-next-line max-len
      // https://www.ag-grid.com/javascript-data-grid/component-filter/#custom-filters-containing-a-popup-element
      popupClassName='ag-custom-component-popup'
    />
    <StyledInput
      value={input}
      onChange={handleInputChange}
      onFocus={handleFocus}
      onBlur={handleBlur}
    />
    {tag ?
      <FilterTag
        color='blue'
        closable={true}
        onClose={handleTagClear}
        isColumnFilter={isColumnFilter}
      >
        {tag}
      </FilterTag> :
      null
    }
    <ApplyButton onClick={handleApply} disabled={!input} isColumnFilter={isColumnFilter}>
      {intl.formatMessage({ id: 'worklist.filters.SAVE_AS_FILTER' })}
    </ApplyButton>
  </Container>;
};

OperatorFilter.propTypes = {
  description: PropTypes.string,
  operators: PropTypes.array,
  onChange: PropTypes.func,
  activeFilters: PropTypes.array,
  prefix: PropTypes.string,
  defaultOperator: PropTypes.string,
  intl: PropTypes.object,
  isColumnFilter: PropTypes.bool,
  info: PropTypes.string,
};

export default injectIntl(OperatorFilter);
